import { CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import classNames from 'classnames';
import React, { ChangeEvent, SyntheticEvent, useState } from 'react';

import { ReactComponent as ArtGetStartedSvg } from '../../../assets/art_get_started.svg';
import {
  DepartmentEnum,
  GetLoggedInUserQuery,
  OnboardingStep,
  useUpdateUserSettingsMutation,
} from '../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum, topBorderStyle } from '../../../theme';
import { DepartmentEnumData } from '../../../utils/enumData';
import { ButtonColor, PlexButton } from '../../shared/primitives/PlexButton';
import { PlexTypography } from '../../shared/primitives/PlexTypography';

interface Props {
  userProfile: NonNullable<GetLoggedInUserQuery['currentUser']>;
  onNext: () => void;
}

interface State {
  name: string;
  jobTitle: string | null | undefined;
  jobDepartment: DepartmentEnum | null | undefined;
}

export const GetStarted = ({ userProfile, onNext }: Props): React.ReactElement => {
  const classes = useStyles();
  const [updateUserSettings, { loading }] = useUpdateUserSettingsMutation();
  const [state, setState] = useState<State>({
    name: userProfile.name,
    jobTitle: userProfile.jobTitle,
    jobDepartment: userProfile.jobDepartment,
  });

  const handleNameChange = (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const text = event.currentTarget.value;
    setState((prevState) => ({ ...prevState, name: text }));
  };

  const handleJobTitleChange = (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const text = event.currentTarget.value;
    setState((prevState) => ({ ...prevState, jobTitle: text }));
  };

  const handleDepartmentChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>): void => {
    const text = event.target.value as string;
    setState((prevState) => ({ ...prevState, jobDepartment: text as DepartmentEnum }));
  };

  return (
    <div className={classes.root}>
      <div className={classes.rootContent}>
        <div className={classes.artContainer}>
          <div>
            <ArtGetStartedSvg />
          </div>
          <PlexTypography className={classes.artTitle} text={"We're so glad you're here"} size={TextSizesEnum.XLarge} />
          <PlexTypography className={classes.artMessage} size={TextSizesEnum.NormalPlus}>
            <span className={classes.spanItem}>{'Tell us a bit about what you do at '}</span>
            <span className={classNames(classes.spanItem, classes.organizationName)}>
              {userProfile.domain?.orgName}
            </span>
            <span className={classes.spanItem}>
              {" and\nwe'll use this information to make sure you're\ngetting the most of our Plex"}
            </span>
          </PlexTypography>
        </div>
        <div className={classes.formContainer}>
          <div className={classes.formInner}>
            <div className={classes.formContent}>
              <PlexTypography className={classes.formTitle} text={'Get started'} size={TextSizesEnum.XXLarge} />
              <PlexTypography className={classes.formMessage} size={TextSizesEnum.Normal}>
                <span className={classes.spanItem}>{'Tell us a bit about what you do at '}</span>
                <span className={classNames(classes.spanItem, classes.organizationName)}>
                  {userProfile.domain?.orgName}
                </span>
              </PlexTypography>
              <TextField
                className={classNames(classes.input, classes.nameInput)}
                label={'Username'}
                type={'name'}
                value={state.name}
                onChange={handleNameChange}
              />
              <TextField
                disabled
                className={classNames(classes.input, classes.nameInput)}
                label={'Email'}
                type={'email'}
                value={userProfile.email}
                contentEditable={false}
              />
              <div className={classNames(classes.input, classes.organizationContainer)}>
                <TextField
                  disabled
                  className={classes.organizationInput}
                  label={'Organization'}
                  value={userProfile.domain?.orgName}
                  contentEditable={false}
                />
                <TextField
                  className={classes.userTitleInput}
                  label={'Your job title'}
                  onChange={handleJobTitleChange}
                  value={state.jobTitle}
                />
              </div>

              <FormControl className={classNames(classes.input, classes.rolePicker)}>
                <InputLabel id={'departmentPicker'}>Department</InputLabel>
                <Select
                  labelId={'departmentPicker'}
                  onChange={handleDepartmentChange}
                  value={state.jobDepartment || ''}
                >
                  {Object.values(DepartmentEnumData).map((department) => (
                    <MenuItem key={department.value} value={department.value}>
                      {department.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className={classes.actionButtonsContainer}>
              {loading && <CircularProgress size={24} />}
              <PlexButton
                className={classes.skipButton}
                label={'Skip'}
                onClick={onNext}
                color={ButtonColor.TransparentBlack}
              />
              <PlexButton
                className={classes.nextButton}
                label={'Next'}
                onClick={(): void => {
                  updateUserSettings({
                    variables: {
                      userSettingsInput: {
                        name: state.name,
                        jobTitle: state.jobTitle,
                        jobDepartment: state.jobDepartment,
                        completedOnboardingStep: OnboardingStep.GetStarted,
                      },
                    },
                    update: (_, { errors }) => {
                      if (!errors?.length) {
                        onNext();
                      } else {
                        console.error('got err when next on get started:', errors);
                      }
                    },
                  });
                }}
                color={ButtonColor.Primary}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      overflowY: 'hidden',
      overflowX: 'auto',
      height: '100vh',
      width: '100%',
      paddingTop: 80,
    },
    rootContent: {
      display: 'flex',
      flexDirection: 'row',
      overflowY: 'auto',
      width: '100%',
      height: '100%',
    },
    artContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      backgroundColor: '#fff',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: 48,
      paddingRight: 48,
    },
    artTitle: {
      color: '#464663',
      fontWeight: 'bold',
      textAlign: 'center',
      marginTop: spacings.xlarge,
    },
    artMessage: {
      display: 'block',
      color: colorPalette.purpleGray._500,
      textAlign: 'center',
      marginTop: spacings.normal,
    },
    formContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      justifyContent: 'center',
    },
    formInner: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      justifyContent: 'space-between',
    },
    formContent: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: 54,
      width: 450,
      marginLeft: 48,
      marginRight: 48,
    },
    formTitle: {
      color: '#464663',
      fontWeight: 600,
      fontSize: 44,
      lineHeight: '53px',
    },
    formMessage: {
      display: 'block',
      color: colorPalette.purpleGray._500,
      marginTop: spacings.small,
      marginBottom: spacings.xxlarge,
    },
    spanItem: {
      display: 'inline',
    },
    organizationName: {
      display: 'inline',
      fontWeight: 'bold',
    },
    input: {
      width: '100%',
      marginTop: spacings.xxlarge,
    },
    nameInput: {
      width: '100%',
      marginTop: spacings.xlarge,
    },
    organizationContainer: {
      display: 'flex',
      flexContainer: 'row',
    },
    organizationInput: {
      flex: 1,
      marginRight: spacings.xlarge / 2,
    },
    userTitleInput: {
      flex: 1,
      marginLeft: spacings.small / 2,
    },
    rolePicker: {
      width: '100%',
      marginBottom: spacings.normal,
    },
    actionButtonsContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      paddingTop: spacings.xlarge,
      paddingBottom: spacings.xlarge,
      paddingRight: spacings.xlarge,
      ...topBorderStyle,
    },
    skipButton: {
      height: 52,
      borderRadius: 36,
    },
    nextButton: {
      height: 52,
      width: 112,
      borderRadius: 36,
    },
  })
);
